import { useCallback, useSyncExternalStore } from "react";

/**
 * The widest viewport still treated as a phone.
 *
 * Chosen to sit just under the 640px tablet breakpoint already used in
 * BoardView.scss, so the two never disagree about which layout a given width
 * gets. Every current phone in portrait (up to ~430 CSS px) is comfortably
 * inside it, and a landscape phone or small tablet gets the column board back.
 */
export const PHONE_VIEWPORT_MAX_WIDTH_PX = 639;

export const PHONE_VIEWPORT_MEDIA_QUERY = `(max-width: ${PHONE_VIEWPORT_MAX_WIDTH_PX}px)`;

/**
 * The widest viewport that still hides the sidebar off-screen as a drawer.
 *
 * Mirrors the `max-width: 991px` breakpoint in AppSidebar.scss. Distinct from
 * the phone breakpoint above: between the two the board is still a kanban, but
 * the sidebar is a drawer rather than a permanent pane — which is why
 * navigation lives in the drawer below this width and in the header above it.
 */
export const DRAWER_VIEWPORT_MAX_WIDTH_PX = 991;

export const DRAWER_VIEWPORT_MEDIA_QUERY = `(max-width: ${DRAWER_VIEWPORT_MAX_WIDTH_PX}px)`;

const subscribeToNothing = (): (() => void) => () => undefined;

/**
 * Tracks a CSS media query as React state.
 *
 * `useSyncExternalStore` rather than an effect with `useState`: the first
 * render then already reports the real match instead of flashing the desktop
 * layout for a frame on a phone.
 *
 * @param query A CSS media query, e.g. `(max-width: 639px)`.
 * @returns Whether the query currently matches. Always `false` where
 *   `matchMedia` is unavailable (a non-DOM bundle evaluation, or an old test
 *   environment), which keeps the desktop layout as the fallback.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener("change", onStoreChange);
      return () => {
        mediaQueryList.removeEventListener("change", onStoreChange);
      };
    },
    [query],
  );

  const getSnapshot = useCallback(
    (): boolean => window.matchMedia(query).matches,
    [query],
  );

  const isSupported =
    typeof window !== "undefined" && typeof window.matchMedia === "function";

  return useSyncExternalStore(
    isSupported ? subscribe : subscribeToNothing,
    isSupported ? getSnapshot : () => false,
    // Server render: nothing is measurable, so fall back to the wide layout.
    () => false,
  );
}

/**
 * Whether the viewport is phone-sized, and so should get the single-column
 * task list instead of the kanban board.
 */
export function useIsPhoneViewport(): boolean {
  return useMediaQuery(PHONE_VIEWPORT_MEDIA_QUERY);
}

/**
 * Whether the sidebar is currently a slide-in drawer rather than a permanent
 * pane. Navigation belongs in the drawer at these widths, because the header
 * has no room for it beside the board switcher.
 */
export function useIsDrawerViewport(): boolean {
  return useMediaQuery(DRAWER_VIEWPORT_MEDIA_QUERY);
}
